import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, Hash, ScanText, Settings2, Sparkles, Users } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { isApprovedFactoryManager } from '../../lib/factoryContext';
import { factoryService, type FactoryCostLedger } from '../../services/factoryService';

type SettingsSection = 'pipeline' | 'enrich' | 'mineru' | 'material-code' | 'staff' | 'cost';

const SETTINGS_TABS = new Set<SettingsSection>([
  'pipeline',
  'enrich',
  'mineru',
  'material-code',
  'staff',
  'cost',
]);

function parseSettingsTab(raw: string | null): SettingsSection {
  const key = String(raw || '').trim() as SettingsSection;
  return SETTINGS_TABS.has(key) ? key : 'pipeline';
}

const sections: {
  id: SettingsSection;
  label: string;
  description: string;
  icon: typeof ScanText;
}[] = [
  { id: 'pipeline', label: '产线闸门', description: 'Owner 验收发送', icon: Settings2 },
  { id: 'enrich', label: '富化画像', description: 'Runtime ADMIN', icon: Sparkles },
  { id: 'mineru', label: '版面解析', description: '文档块切分引擎', icon: ScanText },
  { id: 'material-code', label: '素材码', description: '发码 / 绑定', icon: Hash },
  { id: 'staff', label: '人员', description: '厂员与授权', icon: Users },
  { id: 'cost', label: '成本核算', description: '颗粒工厂总成本', icon: Calculator },
];

export function FactorySettingsDomainPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = parseSettingsTab(searchParams.get('tab'));
  const setActive = (id: SettingsSection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'pipeline') next.delete('tab');
        else next.set('tab', id);
        return next;
      },
      { replace: true },
    );
  };
  const [apiBase, setApiBase] = useState('');
  const [modelVersion, setModelVersion] = useState('vlm');
  const [apiToken, setApiToken] = useState('');
  const [masked, setMasked] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [enrichEdgeBase, setEnrichEdgeBase] = useState('');
  const [enrichAdminToken, setEnrichAdminToken] = useState('');
  const [enrichHasAdminToken, setEnrichHasAdminToken] = useState(false);
  const [enrichAdminMasked, setEnrichAdminMasked] = useState('');
  const [enrichAdminSource, setEnrichAdminSource] = useState('none');
  const [enrichHint, setEnrichHint] = useState('');
  const [autoOwnerAccept, setAutoOwnerAccept] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [bindAccountId, setBindAccountId] = useState('');
  const [costLedger, setCostLedger] = useState<FactoryCostLedger | null>(null);
  const [costError, setCostError] = useState('');
  const [costBusy, setCostBusy] = useState(false);

  useEffect(() => {
    factoryService
      .getMinerUSettings()
      .then((res) => {
        setApiBase(res.mineru.apiBase || '');
        setModelVersion(res.mineru.modelVersion || 'vlm');
        setMasked(res.mineru.apiTokenMasked || '');
        setHasToken(Boolean(res.mineru.hasToken));
      })
      .catch((err) => setMessage(err instanceof Error ? err.message : String(err)));
    factoryService
      .getPipelineSettings()
      .then((res) => {
        setAutoOwnerAccept(Boolean(res.autoRequestOwnerAcceptIfCleanExtract));
      })
      .catch(() => {
        /* soft — older AppFactory */
      });
    factoryService
      .getEnrichSettings()
      .then((res) => {
        setEnrichEdgeBase(res.edgeBase || '');
        setEnrichHasAdminToken(Boolean(res.hasAdminToken));
        setEnrichAdminMasked(res.adminTokenMasked || '');
        setEnrichAdminSource(res.adminTokenSource || 'none');
        setEnrichHint(res.hint || '');
      })
      .catch(() => {
        /* soft — older AppFactory */
      });
  }, []);

  useEffect(() => {
    if (active !== 'cost') return;
    let cancelled = false;
    setCostBusy(true);
    setCostError('');
    factoryService
      .getCostLedger()
      .then((ledger) => {
        if (!cancelled) setCostLedger(ledger);
      })
      .catch((err) => {
        if (!cancelled) setCostError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setCostBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  async function onSavePipeline(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const res = await factoryService.updatePipelineSettings({
        autoRequestOwnerAcceptIfCleanExtract: autoOwnerAccept,
      });
      setAutoOwnerAccept(Boolean(res.pipeline.autoRequestOwnerAcceptIfCleanExtract));
      setMessage(
        autoOwnerAccept
          ? '已开启：提取无失败时自动发送 Owner 验收。'
          : '已关闭自动发送：需在成品智能体台手动发送 Owner 验收。',
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSaveEnrich(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const payload: {
        adminToken?: string;
        edgeBase?: string;
      } = {
        edgeBase: enrichEdgeBase.trim(),
      };
      if (enrichAdminToken.trim()) payload.adminToken = enrichAdminToken.trim();
      const res = await factoryService.updateEnrichSettings(payload);
      setEnrichEdgeBase(res.edgeBase || '');
      setEnrichHasAdminToken(Boolean(res.hasAdminToken));
      setEnrichAdminMasked(res.adminTokenMasked || '');
      setEnrichAdminSource(res.adminTokenSource || 'none');
      setEnrichHint(res.hint || '');
      setEnrichAdminToken('');
      setMessage(res.hint || '富化画像配置已保存。');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSaveMineru(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const payload: { apiBase?: string; modelVersion?: string; apiToken?: string } = {
        apiBase: apiBase.trim(),
        modelVersion: modelVersion.trim(),
      };
      if (apiToken.trim()) payload.apiToken = apiToken.trim();
      const res = await factoryService.updateMinerUSettings(payload);
      setMasked(String(res.mineru.apiTokenMasked || ''));
      setHasToken(Boolean(res.mineru.hasToken));
      setApiToken('');
      setMessage('版面解析配置已保存。');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onCreateCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await factoryService.createMaterialCode(code.trim());
      setMessage(`素材码 ${code.trim()} 已创建（未绑定）。`);
      setCode('');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onBindCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await factoryService.bindMaterialCode(code.trim(), bindAccountId.trim());
      setMessage(`素材码已绑定账户 ${bindAccountId.trim()}。`);
      setCode('');
      setBindAccountId('');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const activeMeta = sections.find((s) => s.id === active) || sections[0];
  const enrichSourceLabel =
    enrichAdminSource === 'settings'
      ? '设置页'
      : enrichAdminSource === 'env'
        ? '服务器环境变量'
        : '未配置';

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold text-ink">设置</h1>
        <p className="mt-1 text-sm text-muted">工厂产线参数、素材码、人员与成本核算（不改学习壳设置页）。</p>
      </header>

      {message ? (
        <p className={`text-sm ${message.includes('已') || message.includes('保存') ? 'text-primary' : 'text-danger'}`}>{message}</p>
      ) : null}

      <div className="flex min-h-[28rem] overflow-hidden rounded-md border border-[#3a4450] bg-[#1e242c] shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
        <nav
          className="flex w-[4.5rem] shrink-0 flex-col gap-1 border-r border-[#3a4450] bg-[#15191f] p-2 sm:w-44"
          aria-label="设置分组"
        >
          {sections.map(({ id, label, description, icon: Icon }) => {
            const selected = active === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActive(id)}
                className={`flex flex-col items-center gap-1 rounded-md px-2 py-3 text-center transition sm:flex-row sm:items-start sm:gap-3 sm:px-3 sm:text-left ${
                  selected
                    ? 'bg-[#2EC4B6]/15 text-[#2EC4B6]'
                    : 'text-[#8B94A3] hover:bg-white/5 hover:text-[#E8ECF1]'
                }`}
                aria-current={selected ? 'page' : undefined}
              >
                <Icon size={20} className="shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-[11px] font-semibold leading-tight sm:text-sm">{label}</span>
                  <span className={`mt-0.5 hidden text-[11px] sm:block ${selected ? 'text-[#2EC4B6]/80' : 'text-[#6B7380]'}`}>
                    {description}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-5">
          <div className="mb-4 border-b border-[#3a4450] pb-3">
            <h2 className="text-base font-semibold text-[#F2F5F8]">{activeMeta.label}</h2>
            <p className="mt-0.5 text-xs text-[#9AA3B0]">{activeMeta.description}</p>
          </div>

          {active === 'pipeline' ? (
            <form className="space-y-4" onSubmit={onSavePipeline}>
              <p className="text-sm text-[#C5CDD8]">
                默认需在「成品智能体」台手动「发送 Owner 验收」。开启下方开关后，产线就绪且
                <span className="text-[#E8ECF1]">没有颗粒提取失败页</span>
                时，会自动发送等待 Owner 授权。
              </p>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#2EC4B6]"
                  checked={autoOwnerAccept}
                  onChange={(e) => setAutoOwnerAccept(e.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#E8ECF1]">
                    无提取失败时自动发送 Owner 验收
                  </span>
                  <span className="mt-1 block text-xs text-[#9AA3B0]">
                    仍有失败页时不会自动发送，需先在结果页重抽再手动发送。
                  </span>
                </span>
              </label>
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
              >
                保存
              </button>
            </form>
          ) : null}

          {active === 'enrich' ? (
            <form className="space-y-4" onSubmit={onSaveEnrich}>
              {!enrichHasAdminToken ? (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  当前未配置 Runtime ADMIN token。RFE 拉不到学科画像，结果页会显示「缺 Runtime 画像」。
                </p>
              ) : (
                <p className="text-xs text-[#9AA3B0]">
                  当前令牌：{enrichAdminMasked || '已配置'}（来源：{enrichSourceLabel}）
                </p>
              )}
              {enrichHint ? <p className="text-xs text-[#8B94A3]">{enrichHint}</p> : null}
              <label className="block text-sm">
                <span className="text-[#C5CDD8]">Runtime Edge Base</span>
                <input
                  className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                  value={enrichEdgeBase}
                  onChange={(e) => setEnrichEdgeBase(e.target.value)}
                  placeholder="https://runtime-edge.revisionbrook.com"
                />
              </label>
              <label className="block text-sm">
                <span className="text-[#C5CDD8]">Runtime ADMIN token（留空不改）</span>
                <input
                  className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                  type="password"
                  autoComplete="off"
                  value={enrichAdminToken}
                  onChange={(e) => setEnrichAdminToken(e.target.value)}
                  placeholder="与 AppFunctor / Runtime 同套 ADMIN token"
                />
              </label>
              <p className="text-xs text-[#6B7380]">
                写入产线库设置；也可用服务器环境变量
                <code className="mx-1 text-[#9AA3B0]">WEBAPPSERVER_RUNTIME_ADMIN_API_TOKEN</code>
                。客户端从不直连 Runtime。
              </p>
              <button
                type="submit"
                disabled={busy}
                className="rounded-md bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
              >
                保存
              </button>
            </form>
          ) : null}

          {active === 'mineru' ? (
            <div className="space-y-3">
              <p className="text-xs text-[#9AA3B0]">当前令牌：{hasToken ? masked || '已配置' : '未配置'}</p>
              <form className="space-y-3" onSubmit={onSaveMineru}>
                <label className="block text-sm">
                  <span className="text-[#C5CDD8]">API Base</span>
                  <input
                    className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                    value={apiBase}
                    onChange={(e) => setApiBase(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-[#C5CDD8]">Model version</span>
                  <input
                    className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                    value={modelVersion}
                    onChange={(e) => setModelVersion(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-[#C5CDD8]">新 Token（留空不改）</span>
                  <input
                    className="mt-1 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                    type="password"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-md bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
                >
                  保存
                </button>
              </form>
            </div>
          ) : null}

          {active === 'material-code' ? (
            <div className="space-y-4">
              <form className="flex flex-wrap gap-2" onSubmit={onCreateCode}>
                <input
                  className="min-w-[12rem] flex-1 rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-sm text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                  placeholder="新码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={busy || !code.trim()}
                  className="rounded-md border border-[#3a4450] px-3 py-2 text-sm text-[#C5CDD8] hover:border-[#2EC4B6]/50 hover:text-[#2EC4B6] disabled:opacity-50"
                >
                  发码
                </button>
              </form>
              <form className="flex flex-wrap gap-2" onSubmit={onBindCode}>
                <input
                  className="min-w-[8rem] flex-1 rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-sm text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                  placeholder="码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <input
                  className="min-w-[12rem] flex-1 rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2 text-sm text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35"
                  placeholder="日记 accountId (uuid)"
                  value={bindAccountId}
                  onChange={(e) => setBindAccountId(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={busy || !code.trim() || !bindAccountId.trim()}
                  className="rounded-md border border-[#3a4450] px-3 py-2 text-sm text-[#C5CDD8] hover:border-[#2EC4B6]/50 hover:text-[#2EC4B6] disabled:opacity-50"
                >
                  绑定
                </button>
              </form>
            </div>
          ) : null}

          {active === 'staff' ? (
            <div className="space-y-3">
              {isApprovedFactoryManager(user) ? (
                <Link to="/factory/staff" className="inline-flex text-sm text-[#2EC4B6] hover:underline">
                  厂员审批与身份管理 →
                </Link>
              ) : (
                <p className="text-sm text-[#9AA3B0]">厂员管理仅工厂管理员可见。</p>
              )}
              <p className="text-xs text-[#8B94A3]">Owner 授权仍在学习壳 Personal：/owner/batches</p>
            </div>
          ) : null}

          {active === 'cost' ? (
            <FactoryCostLedgerPanel
              ledger={costLedger}
              busy={costBusy}
              error={costError}
              onRefresh={() => {
                setCostBusy(true);
                setCostError('');
                factoryService
                  .getCostLedger()
                  .then(setCostLedger)
                  .catch((err) => setCostError(err instanceof Error ? err.message : String(err)))
                  .finally(() => setCostBusy(false));
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatCny(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n <= 0) return '¥0';
  if (n < 0.01) return `¥${n.toFixed(3)}`;
  if (n < 100) return `¥${n.toFixed(2)}`;
  return `¥${n.toFixed(n < 1000 ? 1 : 0)}`;
}

function formatCount(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('zh-CN') : '0';
}

function formatCu(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n <= 0) return '0';
  if (n >= 10_000) {
    const wan = n / 10_000;
    const digits = wan >= 10 ? 0 : 1;
    return `${wan.toFixed(digits)}万`;
  }
  return n.toLocaleString('zh-CN');
}

function FactoryCostLedgerPanel({
  ledger,
  busy,
  error,
  onRefresh,
}: {
  ledger: FactoryCostLedger | null;
  busy: boolean;
  error: string;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#C5CDD8]">
        核算本厂库全部任务：Token 按 Qwen3.7-Plus 价目；FC 按阿里云 CU（规格 × 占用时长，不是调用次数）；存储按标准存储月租；流量按公网流出×拉取倍数。各项自带裕量系数。
      </p>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {busy && !ledger ? <p className="text-sm text-[#9AA3B0]">正在汇总…</p> : null}
      {ledger ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] text-[#8B94A3]">总成本</p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums text-[#F2F5F8]">
                {formatCny(ledger.grandTotalCny)}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-[#8B94A3]">平均单页</p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums text-[#F2F5F8]">
                {ledger.perPageCny == null ? '—' : formatCny(ledger.perPageCny)}
              </p>
            </div>
          </div>
          <p className="text-xs text-[#8B94A3]">
            {formatCount(ledger.batchCount)} 个任务 · {formatCount(ledger.pageCount)} 页
            {ledger.incomplete ? ' · Token 明细尚不完整' : ''}
          </p>
          <dl className="space-y-2 text-sm">
            <div className="flex items-baseline justify-between gap-3 border-t border-[#3a4450] pt-2">
              <dt className="text-[#C5CDD8]">
                Token
                <span className="mt-0.5 block text-[11px] text-[#8B94A3]">
                  {formatCount(ledger.totalTokens)} · 输入 {formatCount(ledger.promptTokens)} / 输出 {formatCount(ledger.completionTokens)} · 裕度 ×{ledger.margin}
                </span>
              </dt>
              <dd className="shrink-0 tabular-nums text-[#E8ECF1]">{formatCny(ledger.tokenCny)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[#C5CDD8]">
                FC
                <span className="mt-0.5 block text-[11px] text-[#8B94A3]">
                  估 {formatCu(ledger.estimatedFcCu)} CU · ¥{ledger.fcRateCnyPerCu ?? 0.00011}/CU
                  · {ledger.fcInstanceVcpu ?? 2}vCPU/{ledger.fcInstanceMemGb ?? 4}GB 占用
                  · 裕度 ×{ledger.margin}
                </span>
              </dt>
              <dd className="shrink-0 tabular-nums text-[#E8ECF1]">{formatCny(ledger.fcCny)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[#C5CDD8]">
                计算小计
                <span className="mt-0.5 block text-[11px] text-[#8B94A3]">
                  (Token + FC) × {ledger.margin}
                </span>
              </dt>
              <dd className="shrink-0 tabular-nums text-[#E8ECF1]">{formatCny(ledger.computeCny)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[#C5CDD8]">
                存储
                <span className="mt-0.5 block text-[11px] text-[#8B94A3]">
                  {ledger.storageGb.toFixed(3)} GB · ¥{ledger.storageRateCnyPerGbMonth}/GB·月 · 裕度 ×{ledger.storageMargin}
                </span>
              </dt>
              <dd className="shrink-0 tabular-nums text-[#E8ECF1]">{formatCny(ledger.storageCny)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[#C5CDD8]">
                流量
                <span className="mt-0.5 block text-[11px] text-[#8B94A3]">
                  {ledger.trafficGb.toFixed(3)} GB · 拉取 ×{ledger.trafficFetchFactor} · ¥{ledger.trafficRateCnyPerGb}/GB · 裕度 ×{ledger.trafficMargin}
                </span>
              </dt>
              <dd className="shrink-0 tabular-nums text-[#E8ECF1]">{formatCny(ledger.trafficCny)}</dd>
            </div>
          </dl>
        </>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={onRefresh}
        className="rounded-md border border-[#3a4450] px-3 py-2 text-sm text-[#C5CDD8] hover:border-[#2EC4B6]/50 hover:text-[#2EC4B6] disabled:opacity-50"
      >
        {busy ? '汇总中…' : '重新核算'}
      </button>
    </div>
  );
}
